import {
  getSubjectBounds,
  loadImageData,
  maskHasTransparentPixels,
} from "../../utils/getBoundingBox";

/**
 * Same opaque-subject bbox pass used by CanvasEditor for the main object.
 * Shrinks an upload overlay's box to the cutout and returns crop metadata
 * so OverlayImageNode can clip transparent padding.
 *
 * @param {{ x: number, y: number, width: number, height: number, sourceLayout?: object|null }} layer
 * @param {string} cutoutUrl
 * @returns {Promise<object|null>} layer patch, or null if no tighten needed
 */
export async function tightenOverlayToSubject(layer, cutoutUrl) {
  if (!layer || !cutoutUrl) return null;

  let mask;
  try {
    mask = await loadImageData(cutoutUrl);
  } catch {
    return null;
  }
  if (!mask?.width || !mask?.height) return null;
  if (!maskHasTransparentPixels(mask, 16)) return null;

  const bounds = getSubjectBounds(mask, 0.9);
  if (!bounds || bounds.width <= 0 || bounds.height <= 0) return null;

  const coverage =
    (bounds.width * bounds.height) / (mask.width * mask.height);
  if (coverage >= 0.97) return null;

  const baseLayout = layer.sourceLayout || {
    x: Number(layer.x) || 0,
    y: Number(layer.y) || 0,
    width: Math.max(1, Number(layer.width) || 1),
    height: Math.max(1, Number(layer.height) || 1),
  };

  const scaleX = baseLayout.width / mask.width;
  const scaleY = baseLayout.height / mask.height;

  return {
    sourceLayout: baseLayout,
    contentCrop: {
      x: bounds.left,
      y: bounds.top,
      width: bounds.width,
      height: bounds.height,
      imageWidth: mask.width,
      imageHeight: mask.height,
    },
    x: Math.round(baseLayout.x + bounds.left * scaleX),
    y: Math.round(baseLayout.y + bounds.top * scaleY),
    width: Math.max(40, Math.round(bounds.width * scaleX)),
    height: Math.max(40, Math.round(bounds.height * scaleY)),
  };
}

/** Restore full-frame layout when Remove BG is turned off. */
export function restoreOverlaySourceLayout(layer) {
  if (!layer?.sourceLayout) {
    return {
      contentCrop: null,
    };
  }
  return {
    contentCrop: null,
    x: layer.sourceLayout.x,
    y: layer.sourceLayout.y,
    width: layer.sourceLayout.width,
    height: layer.sourceLayout.height,
  };
}
